import * as React from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import { Box, Button } from '@mui/material'
import Translations from 'src/@core/layouts/components/Translations'
import { LandState, RodeoCardInformation } from 'src/@types'
import Carousel from 'react-material-ui-carousel'
import { useRouter } from 'next/router'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import RodeoEmptyCard from '../rodeo-empty-card'

interface RodeoCardProps {
  information?: RodeoCardInformation
  landId: number
  status: LandState
}

export default function RodeoCard({ information, landId, status }: RodeoCardProps) {
  const router = useRouter()
  const groupedItems = []

  if (information?.differentCategories) {
    for (let i = 0; i < information?.differentCategories?.length; i += 3) {
      groupedItems.push(information.differentCategories.slice(i, i + 3))
    }
  }

  function capitalizeFirstLetter(str: string): string {
    const stringWithSpaces = str.replace(/_/g, ' ');
    const parts = stringWithSpaces.match(/(.*?)(\d+.*)?$/);

    if (parts) {
      const textPart = parts[1].charAt(0).toUpperCase() + parts[1].slice(1).toLowerCase().trim();
      const numberPart = parts[2] ? parts[2].replace(/ /g, '-') : '';
      
      return textPart + (numberPart ? ' ' + numberPart : '');
    }

    return str;
  }

  const handleRedirectRodeo = () => {
    return router.push(`/lands/details/rodeos/${landId}`)
  }

  function calculateFinalNumber(differentCategories: any[]): number {
    let finalNumber = 0

    for (const category of differentCategories) {
      finalNumber += category.newHeads || 0
      finalNumber -= category.minusHeads || 0
    }

    return finalNumber
  }

  let finalNumber = 0
  if (information?.differentCategories) {
    finalNumber = calculateFinalNumber(information.differentCategories)
  }

  if (
    !information ||
    information.totalHeads === undefined ||
    information.differentCategories === undefined ||
    information.totalHeads === 0
  )
    return <RodeoEmptyCard landId={landId} status={status} />

  return (
    information && (
      <Card
        className='rodeos-card'
        sx={{
          width: '100%',
          height: '495px',
          alignItems: 'center',
          borderRadius: '20px',
          backgroundColor: 'white !important',
        }}
      >
        <CardContent
          className='card-medio'
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Typography
              sx={{ fontSize: '18px', fontWeight: '700', color: 'black', textAlign: 'center', mb: '24px' }}
              className='cards-title'
            >
              <Translations text={'Rodeos'} />
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                width: '100%',
                textAlign: 'center',
                mb: '24px'
              }}
            >
              <Typography sx={{ color: 'black', fontWeight: '700', fontSize: '40px', pr: 2 }}>
                {information.totalHeads
                  .toString()
                  .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                  .replace('.', ':')
                  .replace(',', '.')
                  .replace(':', ',')}
              </Typography>
              {finalNumber === 0 ? (
                <></>
              ) : finalNumber > 0 ? (
                <Box display='flex' alignItems='center' justifyContent='center'>
                  <ArrowDropUpIcon sx={{ verticalAlign: 'middle', color: 'green' }} />
                  <Typography
                    sx={{
                      color: 'green',
                      fontWeight: '800',
                      textAlign: 'center',
                      fontSize: '18px',
                      left: -5
                    }}
                  >
                    {finalNumber
                      .toString()
                      .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                      .replace('.', ':')
                      .replace(',', '.')
                      .replace(':', ',')}
                  </Typography>
                </Box>
              ) : (
                <Box display='flex' alignItems='center' justifyContent='center'>
                  <ArrowDropDownIcon sx={{ verticalAlign: 'middle', color: 'red' }} />
                  <Typography
                    sx={{
                      color: 'red',
                      fontWeight: '800',
                      textAlign: 'center',
                      fontSize: '18px',
                      left: -5
                    }}
                  >
                    {finalNumber *
                      -(1)
                        .toString()
                        .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                        .replace('.', ':')
                        .replace(',', '.')
                        .replace(':', ',')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              paddingLeft: '15px',
              paddingRight: '15px',
              mb: '24px'
            }}
          >
            <Typography sx={{ color: 'black', fontWeight: '600', fontSize: '18px' }}>
              <Translations text={'Heads'} />
            </Typography>
            <Typography sx={{ color: 'black', fontWeight: '600', fontSize: '18px' }}>
              <Translations text={'Categories'} />
            </Typography>
          </Box>
          <Carousel
            height={170}
            autoPlay={false}
            animation='slide'
            indicators={true}
            navButtonsAlwaysVisible={true}
            fullHeightHover={false}
            navButtonsProps={{
              style: {
                display: 'none',
                backgroundColor: 'transparent',
                borderRadius: 0
              }
            }}
          >
            {groupedItems.map((group, index) => (
              <Box key={index}>
                {group.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderRadius: '15px',
                      backgroundColor: `${item.newHeads && item.minusHeads && item.newHeads > item.minusHeads
                        ? 'rgba(40, 154, 56, 0.13)'
                        : item.newHeads && item.minusHeads && item.newHeads < item.minusHeads
                          ? 'rgba(255, 46, 46, 0.16)'
                          : 'rgba(0, 0, 0, 0.1)'
                        }`,
                      mb: '24px',
                      height: '40px',
                      paddingLeft: '20px',
                      paddingRight: '20px'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row'
                      }}
                    >
                      <Typography sx={{ color: 'black', fontWeight: '600', fontSize: '18px' }}>
                        {item.headCount
                          .toString()
                          .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                          .replace('.', ':')
                          .replace(',', '.')
                          .replace(':', ',')}
                      </Typography>
                      {item.newHeads && item.minusHeads && item.newHeads > item.minusHeads && (
                        <Box display='flex' alignItems='center' justifyContent='center'>
                          <ArrowDropUpIcon sx={{ verticalAlign: 'middle', color: 'green' }} />{' '}
                          <Typography
                            sx={{
                              color: 'green',
                              fontWeight: '800',
                              textAlign: 'center',
                              fontSize: 'clamp(8px, 16px, 20px)',
                              left: -5
                            }}
                          >
                            {(item.newHeads - item.minusHeads)
                              .toString()
                              .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                              .replace('.', ':')
                              .replace(',', '.')
                              .replace(':', ',')}
                          </Typography>
                        </Box>
                      )}
                      {item.newHeads && item.minusHeads && item.newHeads < item.minusHeads && (
                        <Box display='flex' alignItems='center' justifyContent='center'>
                          <ArrowDropDownIcon sx={{ verticalAlign: 'middle', color: 'red' }} />{' '}
                          <Typography
                            sx={{
                              color: 'red',
                              fontWeight: '800',
                              textAlign: 'center',
                              fontSize: 'clamp(8px, 16px, 20px)',
                              left: -5
                            }}
                          >
                            {(item.minusHeads + item.newHeads)
                              .toString()
                              .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                              .replace('.', ':')
                              .replace(',', '.')
                              .replace(':', ',')}
                          </Typography>
                        </Box>
                      )}
                      {item.newHeads && !item.minusHeads && (
                        <Box display='flex' alignItems='center' justifyContent='center'>
                          <ArrowDropUpIcon sx={{ verticalAlign: 'middle', color: 'green' }} />{' '}
                          <Typography
                            sx={{
                              color: 'green',
                              fontWeight: '800',
                              textAlign: 'center',
                              fontSize: 'clamp(8px, 16px, 20px)',
                              left: -5
                            }}
                          >
                            {item.newHeads
                              .toString()
                              .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                              .replace('.', ':')
                              .replace(',', '.')
                              .replace(':', ',')}
                          </Typography>
                        </Box>
                      )}
                      {item.minusHeads && !item.newHeads && (
                        <Box display='flex' alignItems='center' justifyContent='center'>
                          <ArrowDropDownIcon sx={{ verticalAlign: 'middle', color: 'red' }} />{' '}
                          <Typography
                            sx={{
                              color: 'red',
                              fontWeight: '800',
                              textAlign: 'center',
                              fontSize: 'clamp(8px, 16px, 20px)',
                              left: -5
                            }}
                          >
                            {item.minusHeads
                              .toString()
                              .replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
                              .replace('.', ':')
                              .replace(',', '.')
                              .replace(':', ',')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ color: 'black', fontSize: 'clamp(10px, 14px, 18px)', textAlign: 'end', ml: 3 }}>
                      <Translations text={capitalizeFirstLetter(item.category)} />
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Carousel>
          <Box sx={{ display: 'flex', justifyContent: 'center', w: '100%', alignItems: 'center', mt: '24px' }}>
            <Button
              variant='outlined'
              sx={{
                color: '#213D36',
                width: '123px',
                padding: '8px, 12px, 8px, 12px',
                fontSize: '16px',
                fontWeight: 700,
                borderRadius: '30px',
                border: '3px solid #213D36'
              }}
              onClick={handleRedirectRodeo}
            >
              <Translations text={'DETAILS'} />
            </Button>
          </Box>
        </CardContent>
      </Card>
    )
  )
}
